import { ShieldCheck } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { PageHeader } from '@/components/layout/page-header'
import { StaffUserManagement } from '@/app/(dashboard)/settings/users/_components/StaffUserManagement'
import { apiClient } from '@/lib/api/client'
import { getHelmSessionUser } from '@/src/lib/auth'
import { useAsyncData } from '@/src/hooks/useAsyncData'
import { LoadingState } from '@/src/components/LoadingState'
import { ErrorState } from '@/src/components/ErrorState'

export function UsersPage() {
  const { data, error, loading, reload } = useAsyncData(async () => {
    const [user, profile] = await Promise.all([
      getHelmSessionUser(),
      apiClient.getProfile().catch(() => null),
    ])
    const isAdmin = !!profile?.is_admin || profile?.role === 'admin'
    const staff = isAdmin ? await apiClient.listHelmUsers().catch(() => null) : null

    return { user, profile, staff, isAdmin }
  }, [])

  // Only the first load replaces the page; a refresh after a change keeps it on screen.
  if (loading && !data) return <LoadingState label="Loading users..." />
  if (error && !data) return <ErrorState error={error} onRetry={reload} />

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        description="Manage Helm staff access and role invitations"
        breadcrumbs={[{ label: 'Settings', href: '/settings' }, { label: 'Users' }]}
      />

      <div className="min-w-0">
        {!data?.isAdmin ? (
          <Alert>
            <ShieldCheck className="h-4 w-4" />
            <AlertDescription>Admin access is required to manage Helm users.</AlertDescription>
          </Alert>
        ) : data.staff && data.user ? (
          <StaffUserManagement
            users={data.staff.users}
            invitations={data.staff.invitations}
            currentUserId={data.user.id}
            onChanged={reload}
          />
        ) : (
          <Alert variant="destructive">
            <AlertDescription>Unable to load Helm users from the API.</AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  )
}
